import {
  ApiError,
  ErrorCode,
  EsocialEventType,
  EsocialFetchStatus,
  StagedEventStatus,
} from '../../api';

/**
 * The words the staged-events screen uses, and the failures it has to explain.
 *
 * A lookup module like [`esocial`](./esocial.ts) next door and
 * [`mailbox`](./mailbox.ts), and here for the same reason both of those are:
 * the backend writes its sentences in English for whoever is integrating, and
 * the person reading this screen is a vyttech analyst who reads pt-BR.
 *
 * Two things are deliberately **not** translated.
 *
 * eSocial's own `descResposta`, which arrives inside `failure_reason`. It is
 * already pt-BR, it is the government's wording rather than ours, and it is
 * what an operator quotes when they open a ticket with the Receita Federal.
 *
 * The five event codes. `S-2200` is what the developer manual says, what the
 * customer's accountant says and what the government's own error messages say;
 * a screen that showed `Admissão` alone would leave somebody translating back.
 * So the code and the Portuguese sit side by side, code first.
 */

export const ESOCIAL_EVENTS_COPY = {
  title: 'Eventos eSocial',
  description:
    'O que o governo já tem registrado sobre o quadro de pessoal de uma empresa cliente: ' +
    'admissões, alterações, afastamentos e desligamentos, buscados sob demanda e guardados ' +
    'aqui para conferência.',

  /**
   * Said on the screen and not only in the API documentation, above everything
   * else, because an analyst who believed a busca had filed movements would
   * stop looking at the fila de análise -- which is the one place movements are
   * actually decided.
   */
  nothingIsApplied:
    'Buscar eventos não altera nenhum cadastro. Nada aqui vira movimentação, nada entra na ' +
    'fila de análise e nada é enviado à seguradora: é só o que o eSocial respondeu, guardado ' +
    'como está.',

  /**
   * Said above the confirm button, and it is the other half of the sentence
   * above. Confirming *does* create movimentações -- and still changes no
   * cadastro, because o que ele cria é pedido, não decisão. An analyst who
   * believed otherwise would either avoid the button or stop reading the fila.
   */
  confirmChangesNothingYet:
    'Confirmar cria movimentações na fila de análise — uma por evento que vira alguma coisa. ' +
    'Nenhum cadastro muda por causa disso: cada movimentação continua esperando a decisão de ' +
    'um analista, exatamente como uma digitada no portal.',

  fetchTitle: 'Buscar eventos',
  fetchDescription:
    'Consulta o eSocial trabalhador por trabalhador — é a única consulta que a produção ' +
    'responde — e guarda os eventos que ainda não temos. O eSocial permite cerca de dez ' +
    'chamadas por dia por empregador, então cada busca cobre uma parte da empresa e a ' +
    'próxima continua de onde esta parou. Buscar duas vezes não duplica nada.',
  company: 'Empresa',
  companyHint:
    'A busca usa o CNPJ cadastrado na empresa. Uma empresa sem CNPJ não pode ser consultada: ' +
    'o eSocial identifica o empregador pelo CNPJ e por mais nada.',
  companyNone: 'Escolha uma empresa',
  companiesLoading: 'Carregando empresas…',
  fetch: 'Buscar eventos',
  fetching: 'Buscando…',
  fetchFailed: 'Não foi possível buscar os eventos.',

  resultTitle: 'Resultado da busca',
  fetched: 'Encontrados no eSocial',
  staged: 'Guardados agora',
  skipped: 'Já tínhamos',
  failed: 'Não vieram',
  refusedTypes: 'Consultas recusadas pelo eSocial',
  esocialResponse: 'Resposta do eSocial',

  /**
   * The run facts that tell "recusada", "não declarou nada" and "acabaram as
   * chamadas" apart. They are three different mornings and, before #345, an
   * operator could not tell them apart from this screen at all -- every one of
   * them looked like a completed run with four zeros on it.
   */
  calls: 'Chamadas usadas',
  workers: 'Trabalhadores consultados',
  workersRemaining: 'Faltam consultar',
  budgetExhausted:
    'A cota de chamadas do dia acabou antes de consultar todo mundo. Os trabalhadores que ' +
    'faltam estão guardados na busca: basta buscar de novo na mesma janela que ela continua ' +
    'de onde parou.',
  rosterFallback:
    'Nenhuma lista de trabalhadores do mês estava guardada para esta empresa, então a busca ' +
    'usou só quem já está no cadastro. Uma busca assim não encontra admissão de quem ainda ' +
    'não conhecemos — capture o fechamento do mês em "Lista de trabalhadores" antes.',
  nothingFiled: 'A empresa não declarou nada na janela consultada.',
  workersConsultedShort: 'Consultados',

  eventsTitle: 'Eventos guardados',
  eventsDescription:
    'Do mais recente para o mais antigo. O CPF aparece mascarado: o número completo fica ' +
    'cifrado no banco e não é devolvido por nenhuma tela.',
  eventsCaption: 'Eventos do eSocial guardados para conferência',
  eventsEmpty: 'Nenhum evento guardado',
  eventsEmptyDescription:
    'Ou a empresa não declarou nada na janela consultada, ou ninguém rodou uma busca ainda. ' +
    'O histórico abaixo diz qual dos dois.',
  eventsLoading: 'Carregando os eventos…',
  eventsFailed: 'Não foi possível carregar os eventos.',

  historyTitle: 'Buscas recentes',
  historyDescription:
    'Quem buscou, para qual empresa, em que janela e no que deu. É daqui que se descobre se ' +
    'uma lista vazia significa "a empresa não declarou nada" ou "o eSocial recusou".',
  historyCaption: 'Histórico de buscas ao eSocial',
  historyEmpty: 'Nenhuma busca ainda',
  historyEmptyDescription: 'Use o botão acima. Cada busca fica registrada na auditoria.',
  historyLoading: 'Carregando o histórico…',
  historyFailed: 'Não foi possível carregar o histórico.',

  confirmTitle: 'Confirmar uma busca',
  confirmDescription:
    'Lê os eventos guardados por uma busca, traduz cada um na movimentação que ele pede e a ' +
    'envia para a fila de análise. Uma busca se confirma uma vez só.',
  confirmContract: 'Contrato',
  confirmContractHint:
    'O eSocial responde por CNPJ do empregador e não diz a qual contrato a pessoa pertence. ' +
    'Por isso o contrato é escolhido aqui, e tem que ser da empresa consultada.',
  confirmContractNone: 'Escolha um contrato',
  confirmContractsLoading: 'Carregando contratos…',
  confirmFetch: 'Busca',
  confirmFetchNone: 'Escolha uma busca ainda não confirmada',
  confirmFetchHint:
    'Só aparecem aqui as buscas que guardaram algum evento e ainda não foram confirmadas.',
  confirm: 'Confirmar e enviar para análise',
  confirming: 'Confirmando…',
  confirmFailed: 'Não foi possível confirmar a busca.',
  confirmNothingToDo: 'Nenhuma busca pendente de confirmação para esta empresa.',
  committed: 'Viraram movimentação',
  noAction: 'Sem ação necessária',
  refusedEvents: 'Recusadas',
  confirmedBy: 'Confirmada por',
  confirmedAt: 'Confirmada em',
  outcome: 'Resultado',
  outcomeReason: 'Motivo',

  allTypes: 'Todos os tipos',
  type: 'Tipo',
  eventDate: 'Data do evento',
  person: 'Pessoa',
  identifier: 'Identificador no eSocial',
  receipt: 'Recibo',
  stagedAt: 'Guardado em',
  window: 'Janela consultada',
  who: 'Quem buscou',
  when: 'Quando',
  status: 'Situação',
  numbers: 'Números',
  coverage: 'Cobertura',
  reason: 'Motivo',
  noCompany: 'Escolha uma empresa para buscar.',
} as const;

/**
 * The five codes, each with what it is in Portuguese.
 *
 * Code first, because the code is the shared vocabulary: it is what the
 * developer manual, the customer's accountant and the eSocial error messages
 * all use, and a screen that hid it would leave somebody translating back.
 */
export const EVENT_TYPE_LABELS: Record<EsocialEventType, string> = {
  [EsocialEventType.S_2200]: 'S-2200 · Admissão',
  [EsocialEventType.S_2205]: 'S-2205 · Alteração cadastral',
  [EsocialEventType.S_2206]: 'S-2206 · Alteração contratual',
  [EsocialEventType.S_2230]: 'S-2230 · Afastamento',
  [EsocialEventType.S_2299]: 'S-2299 · Desligamento',
};

/**
 * What became of one event, in the four words a fila can be read by.
 *
 * `Sem ação` and `Recusada` are deliberately different words rather than one
 * "não virou nada": one of them is nothing to do and the other is somebody's
 * problem, and a screen that spelled them alike would teach an analyst to skip
 * both.
 */
export const EVENT_STATUS_LABELS: Record<StagedEventStatus, string> = {
  [StagedEventStatus.STAGED]: 'Guardado',
  [StagedEventStatus.COMMITTED]: 'Virou movimentação',
  [StagedEventStatus.NO_ACTION]: 'Sem ação necessária',
  [StagedEventStatus.REFUSED]: 'Recusada',
};

/** The tone each outcome is shown in. `Guardado` is neutral: nothing decided yet. */
export const EVENT_STATUS_TONES: Record<
  StagedEventStatus,
  'ok' | 'warning' | 'blocking' | 'muted'
> = {
  [StagedEventStatus.STAGED]: 'muted',
  [StagedEventStatus.COMMITTED]: 'ok',
  [StagedEventStatus.NO_ACTION]: 'muted',
  [StagedEventStatus.REFUSED]: 'blocking',
};

export const FETCH_STATUS_LABELS: Record<EsocialFetchStatus, string> = {
  [EsocialFetchStatus.COMPLETED]: 'Concluída',
  [EsocialFetchStatus.PARTIAL]: 'Parcial',
  [EsocialFetchStatus.FAILED]: 'Falhou',
};

/**
 * The tone each run is shown in.
 *
 * `completed` is green even when it found nothing, and that is the point: an
 * employer who declared nothing in the window is an answer, and a screen that
 * painted it as a problem would teach people to stop reading the colour.
 */
export const FETCH_STATUS_TONES: Record<EsocialFetchStatus, 'ok' | 'warning' | 'blocking'> = {
  [EsocialFetchStatus.COMPLETED]: 'ok',
  [EsocialFetchStatus.PARTIAL]: 'warning',
  [EsocialFetchStatus.FAILED]: 'blocking',
};

/**
 * Our own sentence for the refusals this screen can actually provoke.
 *
 * Keyed on the error code and on the field the envelope names -- both of which
 * the API documents and promises to keep -- rather than on the English message,
 * which is written for whoever is integrating and may be reworded.
 *
 * `undefined` means "we have nothing better to say than the server did", and the
 * generic error banner shows the server's own message.
 */
export function fetchFailureMessage(error: unknown): string | undefined {
  if (!(error instanceof ApiError)) {
    return undefined;
  }
  if (error.code === ErrorCode.SERVICE_UNAVAILABLE) {
    return (
      'Este ambiente não tem cliente eSocial configurado, então não há o que buscar. ' +
      'Falar com o governo é algo que cada ambiente liga de propósito: configure ' +
      'I8LIFE_ESOCIAL_BACKEND e I8LIFE_ESOCIAL_ENVIRONMENT.'
    );
  }
  if (error.details.some((detail) => detail.field === 'company_id')) {
    return (
      'A empresa escolhida não tem CNPJ cadastrado. O eSocial identifica o empregador pelo CNPJ ' +
      'e por mais nada, então não há consulta a fazer: cadastre o CNPJ da empresa e busque de novo.'
    );
  }
  if (error.details.some((detail) => detail.field === 'certificate')) {
    return (
      'Não há certificado A1 em uso nesta plataforma, então não há como nos identificarmos ' +
      'perante o eSocial. Instale o certificado na tela de certificado digital e busque de novo.'
    );
  }
  if (error.isNotFound) {
    return 'A empresa escolhida não existe mais. Atualize a página e escolha outra.';
  }
  return undefined;
}

/**
 * Our sentence for the refusals the confirm button can provoke.
 *
 * Keyed on the code and on the field the envelope names, like the one above, and
 * for the same reason: the English message is written for whoever is
 * integrating and may be reworded without the contract changing.
 */
export function confirmFailureMessage(error: unknown): string | undefined {
  if (!(error instanceof ApiError)) {
    return undefined;
  }
  if (error.code === ErrorCode.CONFLICT) {
    return (
      'Esta busca já foi confirmada, e as movimentações dela já estão na fila de análise. ' +
      'Confirmar de novo pediria cada uma delas uma segunda vez — veja o resultado da ' +
      'confirmação anterior no histórico abaixo.'
    );
  }
  if (error.details.some((detail) => detail.field === 'contract_id')) {
    return (
      'O contrato escolhido não serve para esta busca: ele precisa ser da mesma empresa que ' +
      'foi consultada no eSocial. Escolha um contrato dessa empresa.'
    );
  }
  return undefined;
}
